import { useCallback, useEffect, useMemo, useState } from "react";
import logger from "@/lib/logger";
import { useThrottledFlush } from "@/components/useThrottledFlush";
import { useLatestJournal } from "@/components/dashboard/useLatestJournal";
import { useTailFollow } from "@/components/useTailFollow";
import { cn, fetcherIgnore404, formatDate } from "@/lib/utils";
import { useLocalStorage } from "@/components/useLocalStorage";
import {
  createEventSource,
  EventSourceHandle,
  EventSourceMessage,
  EventSourceMessageJournal
} from "@/components/dashboard/createEventSource";
import { App, Host, JSONSafe, TaskType } from "@/types";
import {
  ChevronsDown,
  ChevronsUp,
  Database,
  Package,
  Rocket,
  SatelliteDish,
  Server,
  Text,
  Trash
} from "lucide-react";
import useSWR, { mutate } from "swr";
import { ObjectLink } from "@/components/OptionalLink";

const hash = (message: EventSourceMessageJournal) => {
  return `${message.createdAt.toISOString()}-${message.message}`;
};

const DEFAULT_ICON = <Text className="w-4 h-4" strokeWidth={1} />;

const TASK_TYPE_TO_ICON: Record<TaskType, React.ReactNode | undefined> = {
  "build:app": <Package className="w-4 h-4" strokeWidth={1} />,
  test: <SatelliteDish className="w-4 h-4" strokeWidth={1} />,
  "prepare:host": <Server className="w-4 h-4" strokeWidth={1} />,
  "deploy:app": <Rocket className="w-4 h-4" strokeWidth={1} />,
  "backup:host": <Database className="w-4 h-4" strokeWidth={1} />,
  "backup:app": <Database className="w-4 h-4" strokeWidth={1} />,
  launch: <Rocket className="w-4 h-4" strokeWidth={1} />,
  "provider:host:create": <Server className="w-4 h-4" strokeWidth={1} />,
  "provider:host:delete": <Server className="w-4 h-4" strokeWidth={1} />,
  "remove:app": <Trash className="w-4 h-4" strokeWidth={1} />,
  "period:minute": undefined,
  "period:hour": undefined,
  "period:day": undefined,
  "period:week": undefined,
  "period:month": undefined,
  "system:message": undefined
};

/**
 * We can't deduplicate by id, because when messages
 * are streamed from the backend, they don't have the same
 * ids as the ones we get from the db.
 */
const deduplicate = (messages: EventSourceMessageJournal[]) => {
  const seen = new Set<string>();
  const result = messages
    .filter(message => {
      if (seen.has(hash(message))) {
        return false;
      } else {
        seen.add(hash(message));
        return true;
      }
    })
    .sort((a, b) => b.createdAt.toISOString().localeCompare(a.createdAt.toISOString()));
  return result;
};

export const AppLink = ({
  appId,
  name,
  onClick
}: {
  appId?: string | null;
  name?: string;
  onClick?: () => void;
}) => {
  const { data: app } = useSWR<JSONSafe<App>>(
    appId && !name ? `/api/app/${appId}` : null,
    fetcherIgnore404
  );
  const displayName = name ?? app?.name;
  if (!displayName) {
    return null;
  }

  return (
    <ObjectLink href={`/app/${appId}`} type="app" onClick={onClick}>
      {displayName}
    </ObjectLink>
  );
};

export const HostLink = ({
  hostId,
  name,
  onClick
}: {
  hostId?: string | null;
  name?: string;
  onClick?: () => void;
}) => {
  const { data: host } = useSWR<JSONSafe<Host>>(
    hostId && !name ? `/api/host/${hostId}` : null,
    fetcherIgnore404
  );
  const displayName = name ?? host?.name;
  if (!displayName) {
    return null;
  }

  return (
    <ObjectLink href={`/host/${hostId}`} type="host" onClick={onClick}>
      {displayName}
    </ObjectLink>
  );
};

export const JournalDrawer = ({
  maxMessages = 30,
  sidebarCollapsed = false
}: {
  maxMessages?: number;
  sidebarCollapsed?: boolean;
}) => {
  const [open, setOpen] = useLocalStorage<boolean>("journalDrawerOpen", false);
  const [logs, setLogs] = useState<EventSourceMessageJournal[]>([]);
  const { latest } = useLatestJournal(true, undefined, open ? 1000 : undefined, maxMessages);

  useEffect(() => {
    setLogs(logs => deduplicate([...logs, ...latest]).slice(0, maxMessages));
  }, [latest, maxMessages]);

  // Render oldest-first (newest at the bottom) and keep the latest line in view.
  const displayLogs = useMemo(() => [...logs].reverse(), [logs]);
  const [isDisplayLogs, setDisplayLogs] = useState(open);
  // Re-pin to the bottom on `isDisplayLogs`, not `open`: the rows only mount once
  // `isDisplayLogs` turns true, one render after `open` flips. Following `open`
  // would scroll an empty container and leave the list stuck at the top until the
  // next data change (e.g. the /api/journal/latest fetch) re-ran the effect.
  const { scrollRef, handleScroll } = useTailFollow([displayLogs, isDisplayLogs, open], open);

  // Keep the list mounted briefly while the drawer slides closed (matches the
  // 300ms transition), then unmount it so a flood of streamed messages does not
  // re-render up to `maxMessages` rows while the drawer is closed.
  useEffect(() => {
    if (open) {
      setDisplayLogs(true);
      return;
    }
    const timeout = setTimeout(() => setDisplayLogs(false), 300);
    return () => clearTimeout(timeout);
  }, [open]);

  // Throttle updates to the list, so for a burst of events we only re-render once.
  const addLog = useThrottledFlush<EventSourceMessageJournal>(
    useCallback(
      pending => setLogs(logs => deduplicate([...pending, ...logs]).slice(0, maxMessages)),
      [maxMessages]
    )
  );

  const consumeMessage = useCallback(
    (message: JSONSafe<EventSourceMessage>) => {
      if (message.type === "invalidate") {
        logger.verbose("Invalidate API path", message.path);
        mutate(message.path);
        // Also mutate all with filters
        if (message.path.indexOf("?") === -1) {
          mutate(key => typeof key === "string" && key.startsWith(message.path + "?"));
        }
        return;
      }

      addLog({
        ...message,
        createdAt: new Date(message.createdAt)
      });
    },
    [addLog]
  );

  useEffect(() => {
    let handle: EventSourceHandle | undefined;

    handle = createEventSource(
      (error: Event) => logger.warn("Event source connection failed", JSON.stringify(error)),
      consumeMessage
    );

    // On document press ESC key, close the drawer
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleEsc);
    return () => {
      handle?.close();
      document.removeEventListener("keydown", handleEsc);
    };
  }, [setOpen, consumeMessage]);

  const lastMessage = useMemo(() => {
    if (
      logs &&
      logs.length > 0 &&
      logs[0]?.message &&
      // eslint-disable-next-line react-hooks/purity
      new Date(logs[0].createdAt).getTime() > Date.now() - 1000 * 60
    ) {
      return logs[0];
    } else {
      return undefined;
    }
  }, [logs]);

  return (
    <>
      {!open && (
        <div
          className={cn(
            "fixed z-50 h-[23px] flex items-start rounded-b-md bg-card dark:bg-gray-800 bg-clip-border cursor-pointer outline-hidden select-none group",
            "-translate-x-1/2 left-1/2 outline-1 outline-primary",
            sidebarCollapsed ? "sm:left-[calc(50%+1.75rem)]" : "sm:left-[calc(50%+5.5rem)]"
          )}
          onClick={() => {
            setOpen(true);
          }}
        >
          {lastMessage ? (
            <div className="py-0 mx-0 pl-2 pr-1 pt-[3px] pb-[4px] flex items-center gap-1 bg-card dark:bg-gray-800 rounded-b-md">
              <span className="dark:text-white">
                {(lastMessage.taskType && TASK_TYPE_TO_ICON[lastMessage.taskType]) ??
                  DEFAULT_ICON}
              </span>
              <span className="dark:text-white text-xs line-clamp-1">
                {lastMessage.message}
              </span>
            </div>
          ) : null}

          <button
            className={cn(
              lastMessage ? "pl-2" : "pl-2",
              "pr-2 mt-[-1px] bg-primary text-primary-foreground/60 group-hover:text-primary-foreground text-sm text-nowrap h-6 flex items-center gap-0.5",
              lastMessage ? "rounded-br-md" : "rounded-b-md"
            )}
            onClick={() => setOpen(true)}
          >
            {lastMessage ? null : <ChevronsDown className="w-4 h-4" />}
            <span className="text-primary-foreground text-xs">Open Log</span>
            {lastMessage ? null : <ChevronsDown className="w-4 h-4" />}
          </button>
        </div>
      )}

      <div
        className={cn(
          "fixed z-50 top-0 left-0 w-full bg-card dark:bg-gray-800 text-foreground transform transition-transform duration-300 ease-in-out",
          open ? "translate-y-0" : "-translate-y-full",
          sidebarCollapsed
            ? "sm:left-14 sm:w-[calc(100%-3.5rem)]"
            : "sm:left-44 sm:w-[calc(100%-11rem)]"
        )}
      >
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="overflow-y-auto p-2 max-h-[380px] border-b-1 border-primary"
        >
          {isDisplayLogs &&
            displayLogs.map((log, index, all) => (
              <div
                key={log.id + "-" + index}
                className={cn(
                  "py-1 flex items-center gap-1 text-xs",
                  index === all.length - 1
                    ? null
                    : "border-b border-color border-muted-foreground/20"
                )}
              >
                <span className="opacity-60 min-w-[4.3em] text-right shrink-0">
                  {formatDate(log.createdAt, "HH:mm:ss")}
                </span>{" "}
                <span className={cn("opacity-60 shrink-0")}>
                  {(log.taskType ? TASK_TYPE_TO_ICON[log.taskType] : undefined) ??
                    DEFAULT_ICON}
                </span>
                <span className="flex-1 min-w-0 line-clamp-1">{log.message}</span>
                {log.appId ? (
                  <span className="opacity-60 shrink-0 ml-2 flex items-center">
                    <AppLink
                      appId={log.appId}
                      name={log.appName}
                      onClick={() => setOpen(false)}
                    />
                  </span>
                ) : null}
                {log.hostId && !log.appId ? (
                  <span className="opacity-60 shrink-0 ml-2 flex items-center">
                    <HostLink
                      hostId={log.hostId}
                      name={log.hostName}
                      onClick={() => setOpen(false)}
                    />
                  </span>
                ) : null}
              </div>
            ))}

          {logs.length === 0 && (
            <div className="text-center text-sm text-muted-foreground">
              No recent activity found
            </div>
          )}
        </div>

        <div className="flex justify-center group absolute bottom-0 left-0 right-0">
          <button
            className="px-2 py-0.5 bg-primary text-primary-foreground rounded-t-md text-sm flex items-center gap-1"
            onClick={() => setOpen(!open)}
          >
            <ChevronsUp className="w-4 h-4 text-primary-foreground/60 group-hover:text-primary-foreground" />
            Close Log{" "}
            <ChevronsUp className="w-4 h-4 text-primary-foreground/60 group-hover:text-primary-foreground" />
          </button>
        </div>
      </div>
    </>
  );
};
